import React, { useCallback, useEffect, useMemo, useState } from 'react';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import { Link, useHistory } from 'react-router-dom';
import cx from 'classnames';
import PropTypes from 'prop-types';

import { BonusTitle } from '@atlas/components';
import { get, isEmptyCollection, SiteConfigManager } from '@atlas/core';
import {
  casinoRoutes,
  CLIENT_PROFILE_COMPONENT_TYPES,
  clientProfileRoutes,
  COMPONENT_TYPES,
  paymentRoutes,
} from '@atlas/routing';
import { Button, EmptyStateView, Icon, Label } from '@atlas/ui';

import { statusOrder } from 'common/constants';
import {
  FINISHED_BONUS_DETAILS_SOURCE,
  isClientBonusFinishedDetailsStatus,
  isClientFreespinFinishedDetailsStatus,
} from 'common/Notifications/Popups/FinishedBonusDetails/finishedBonusDetailsShared';
import { showFinishedBonusDetails } from 'common/Notifications/Popups/FinishedBonusDetails/showFinishedBonusDetails';
import { defaultRoutes } from 'components/MainRouter/constants';
import useFormatter from 'hooks/useFormatter';

import {
  BONUS_FILTER_CHIPS,
  BONUS_FILTERS,
  DEPOSIT_LIKE_TYPES,
  filterByChip,
  getBonusSortEndMs,
  getBonusUiGroup,
  getFreespinSortEndMs,
  getFreespinUiGroup,
  isBonusTemplate,
  isSportBonus,
  sortFeedItems,
  UI_GROUP,
} from '../shared/bonusFeedUtils';
import { resolveBonusImageUrl } from '../shared/bonusImage';
import { buildCasinoGamePath, getFreespinGameId } from '../shared/casinoGameUrl';
import { buildFreeSpinDepositState } from '../shared/freeSpinDepositState';

import BonusChip from './BonusChip';
import { buildProfileBonusCardProps } from './profileBonusCardProps';
import ProfileBonusCardWithLiveTimer from './ProfileBonusCardWithLiveTimer';

const { CASINO, CLIENT_PROFILE } = COMPONENT_TYPES;
const { bonuses, payment } = clientProfileRoutes;
const { deposit } = paymentRoutes;
const casinoGamePath = casinoRoutes.game;

const PAGE_SIZE = 10;

const toBonusFeedItem = (row, sourcePrefix) => ({
  key: `bonus:${sourcePrefix}:${row.id}`,
  kind: 'bonus',
  row,
  sortEndMs: getBonusSortEndMs(row),
  uiGroup: getBonusUiGroup(row),
});

const toFreespinFeedItem = row => ({
  key: `freespin:${row.id}`,
  kind: 'freespin',
  row,
  sortEndMs: getFreespinSortEndMs(row),
  uiGroup: getFreespinUiGroup(row),
});

export const sortBonusesList = (feed, visibleCount) =>
  [...feed]
    .sort(
      (
        { row: { bonusStatus: bonusStatusA, status: statusA } = {}, sortEndMs: sortEndMsA },
        { row: { bonusStatus: bonusStatusB, status: statusB } = {}, sortEndMs: sortEndMsB },
      ) => {
        const statusPrev = bonusStatusA ?? statusA;
        const statusNext = bonusStatusB ?? statusB;
        const statusDiff = statusOrder[statusPrev] - statusOrder[statusNext];
        if (statusDiff !== 0) return statusDiff;
        return (sortEndMsA ?? Infinity) - (sortEndMsB ?? Infinity);
      },
    )
    .slice(0, visibleCount);

const buildFeedItems = ({ availableTemplates, clientBonuses, freespins, sortBonuses }) => {
  const templateIdsWithInstance = new Set(
    clientBonuses
      .filter(
        i =>
          i.bonusTemplateId != null &&
          (DEPOSIT_LIKE_TYPES.has(i.type) || getBonusUiGroup(i) !== UI_GROUP.FINISHED),
      )
      .map(i => i.bonusTemplateId),
  );

  const dedupedTemplates = availableTemplates.filter(
    template => !templateIdsWithInstance.has(template.id),
  );

  const items = [
    ...clientBonuses.map(row => toBonusFeedItem(row, 'instance')),
    ...dedupedTemplates.map(row => toBonusFeedItem(row, 'template')),
    ...freespins.map(toFreespinFeedItem),
  ];

  const visibleItems = sortBonuses ? items.filter(i => i.uiGroup !== UI_GROUP.FINISHED) : items;
  return sortFeedItems(visibleItems);
};

const getActiveChipUrl = () => {
  const params = new URLSearchParams(location.search);
  return params.get('filter') || BONUS_FILTERS.ALL;
};

const { BONUSES } = CLIENT_PROFILE_COMPONENT_TYPES;

const BonusList = ({
  activateBonus,
  activateFreespin,
  availableTemplates,
  cancelFreespin,
  clientBonuses,
  deactivateBonus,
  freespins = [],
  hideEmptyState = false,
  wallet,
}) => {
  const history = useHistory();
  const clientProfileTabs = get(SiteConfigManager.getComponentOptions(CLIENT_PROFILE), 'tabs');
  const bonusesOptions = clientProfileTabs.find(({ type }) => type === BONUSES);

  const { isExpandedOnMobile } = bonusesOptions;

  const hideSportBonuses = SiteConfigManager.getConfig('config.config.general.hideSportBonuses');
  const hideCompletedFromAll = SiteConfigManager.getConfig(
    'config.config.general.hideCompletedBonuses', // final config name pending confirmation from CMS
  );
  const hideBonusCancel = SiteConfigManager.getConfig('config.config.general.hideBonusCancel');
  const sortBonuses = SiteConfigManager.getConfig('config.config.general.sortBonuses');
  const enableFooterForAllPages = SiteConfigManager.getConfig(
    'config.config.general.enableFooterForAllPages',
  );

  const clientProfilePath = SiteConfigManager.getComponentPath(CLIENT_PROFILE);
  const casinoPath = SiteConfigManager.getComponentPath(CASINO, 'casino');
  const depositPath = `${clientProfilePath}/${payment}/${deposit}`;
  const bonusesPath = `${clientProfilePath}/${bonuses}`;

  const [selectedFilter, setSelectedFilter] = useState(getActiveChipUrl);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const { currencyFormatter } = useFormatter();

  const selectFilter = useCallback(
    value => {
      const nextFilter = value || BONUS_FILTERS.ALL;

      setSelectedFilter(nextFilter);
      setVisibleCount(PAGE_SIZE);

      const params = new URLSearchParams(window.location.search);

      if (nextFilter === BONUS_FILTERS.ALL) {
        params.delete('filter');
      } else {
        params.set('filter', nextFilter);
      }
      const newSearch = params.toString();
      if (newSearch !== window.location.search.substring(1)) {
        history.replace({
          pathname: window.location.pathname,
          search: newSearch ? `?${newSearch}` : '',
        });
      }
    },
    [history],
  );
  const baseFeed = useMemo(
    () => buildFeedItems({ availableTemplates, clientBonuses, freespins, sortBonuses }),
    [availableTemplates, clientBonuses, freespins, sortBonuses],
  );

  const sportFiltered = useMemo(
    () => (hideSportBonuses ? baseFeed.filter(i => !isSportBonus(i.row)) : baseFeed),
    [baseFeed, hideSportBonuses],
  );

  const filteredFeed = useMemo(
    () => filterByChip(sportFiltered, selectedFilter, Boolean(hideCompletedFromAll)),
    [hideCompletedFromAll, selectedFilter, sportFiltered],
  );

  const visibleItems = useMemo(
    () => sortBonusesList(filteredFeed, visibleCount),
    [filteredFeed, visibleCount],
  );

  const hasNextPage = visibleCount < filteredFeed.length;

  const loadMore = useCallback(
    () => setVisibleCount(c => Math.min(c + PAGE_SIZE, filteredFeed.length)),
    [filteredFeed.length],
  );

  const [sentryRef, { rootRef }] = useInfiniteScroll({
    hasNextPage,
    loading: false,
    onLoadMore: loadMore,
    rootMargin: '320px 0px',
  });

  useEffect(() => {
    const scrollRoot = typeof document !== 'undefined' ? document.getElementById('content') : null;
    if (scrollRoot) rootRef(scrollRoot);
    return () => rootRef(null);
  }, [rootRef]);

  const openDepositForBonus = useCallback(
    row => {
      // Deposit freespins push a freespin-specific cashier state (the generic preselectBonus
      // does not preselect freespins); deposit / reload-deposit bonuses use preselectBonus.
      if (row.freeSpinSubType) {
        history.push({ pathname: depositPath, state: buildFreeSpinDepositState(row) });
        return;
      }

      const isInstance = !isBonusTemplate(row);
      const templateId = isInstance ? row.bonusTemplateId : row.id;
      const instanceId = isInstance ? row.id : null;

      history.push({
        pathname: depositPath,
        state: {
          preselectBonus: {
            bonusId: templateId ?? null,
            bonusInstanceId: instanceId,
          },
        },
      });
    },
    [depositPath, history],
  );

  const openFinishedDetailsPopup = useCallback(
    item => {
      const { kind, row } = item;
      const imageUrl = resolveBonusImageUrl(row.imageLink) || '';

      if (kind === 'freespin') {
        showFinishedBonusDetails({
          freeSpin: row,
          imageUrl,
          sourceType: FINISHED_BONUS_DETAILS_SOURCE.CLIENT_FREESPIN,
        });

        return;
      }

      showFinishedBonusDetails({
        bonus: row,
        imageUrl,
        sourceType: FINISHED_BONUS_DETAILS_SOURCE.CLIENT_BONUS,
        wallet,
      });
    },
    [wallet],
  );

  const openBonusDetails = useCallback(
    item => {
      const { kind, row } = item ?? {};
      const id = row?.id;
      if (id == null) return;

      const isFinished =
        kind === 'freespin'
          ? isClientFreespinFinishedDetailsStatus(row.status)
          : isClientBonusFinishedDetailsStatus(row.status);

      if (isFinished) {
        openFinishedDetailsPopup(item);

        return;
      }

      // Carry the active ?filter into the detail URL so the back button can restore it
      // (the list reads the chip selection from the query - without this the filter resets to ALL).
      history.push({ pathname: `${bonusesPath}/${id}`, search: history.location.search });
    },
    [bonusesPath, history, openFinishedDetailsPopup],
  );

  const onScrollToTop = () => {
    const scrollRoot = document.querySelector('.bonus-history-content-to-scroll');

    if (!scrollRoot) return;

    requestAnimationFrame(() => {
      scrollRoot.scrollTo({
        behavior: 'smooth',
        left: 0,
        top: 0,
      });
    });
  };

  const activateForItem = useCallback(
    item => {
      if (item?.row?.id == null) return;
      if (item.kind === 'freespin') {
        activateFreespin?.(item.row.id, onScrollToTop);
        return;
      }
      activateBonus?.(item.row, onScrollToTop);
    },
    [activateBonus, activateFreespin],
  );

  // REMOVE (unlocked) / CANCEL (activated) -> cancel. Freespins cancel through the freespin
  // service (client-freespin:cancel); bonuses through the bonus service. Both open the
  // shared Bonus Cancel pop-up (FT-8086).
  const cancelForItem = useCallback(
    item => {
      if (item?.row?.id == null) return;
      if (item.kind === 'freespin') {
        cancelFreespin?.(item.row.id);
        return;
      }
      deactivateBonus?.(item.row);
    },
    [cancelFreespin, deactivateBonus],
  );

  // PLAY (activated) -> open the eligible game in bonus-money mode for freespins; fall back to
  // the casino landing for bonuses or when a freespin has no game configured.
  const playForItem = useCallback(
    item => {
      const gameId = item?.kind === 'freespin' ? getFreespinGameId(item.row) : undefined;
      history.push(
        gameId ? buildCasinoGamePath(casinoPath, casinoGamePath, gameId) : defaultRoutes.casino,
      );
    },
    [casinoPath, history],
  );

  const isFeedEmpty = isEmptyCollection(baseFeed);

  const visibleChips = sortBonuses
    ? BONUS_FILTER_CHIPS.filter(({ id }) => id !== BONUS_FILTERS.FINISHED)
    : BONUS_FILTER_CHIPS;

  if (isFeedEmpty && hideEmptyState) {
    return null;
  }

  const isHideSportBonus = !enableFooterForAllPages && !hideSportBonuses;

  return (
    <>
      <div
        className={cx(
          'bonus-card-chips-list',
          'chips-list',
          (isExpandedOnMobile || hideSportBonuses) && 'expanded-style',
        )}
      >
        {visibleChips.map(({ id, message }) => (
          <BonusChip
            disabled={selectedFilter === id}
            key={id}
            selectedStatus={selectedFilter}
            setSelectedStatus={selectFilter}
            status={id}
            statusMessage={message}
          />
        ))}
      </div>
      {isFeedEmpty || isEmptyCollection(filteredFeed) ? (
        <div className="bonus-list-empty">
          <EmptyStateView
            className="empty-center"
            description="bonus_empty_message"
            title="bonus_empty_title"
          >
            {isFeedEmpty && (
              <div className="bonus-list-empty__cta">
                <Link className="primary link-wrapper" to={defaultRoutes.casino}>
                  <Button className="button" label="go_to_casino" size="medium" type="primary" />
                </Link>
              </div>
            )}
          </EmptyStateView>
        </div>
      ) : (
        <div
          className={cx(
            'bonus-card-list-wrapper bonus-history-list',
            enableFooterForAllPages && 'hidden-footer',
            isHideSportBonus && 'bonus-list--bottom-space',
          )}
        >
          {visibleItems.map(item => {
            const formatter = value => currencyFormatter.format(value);
            const { countdownEndMs, name, nameHighlighted, ...cardProps } =
              buildProfileBonusCardProps(
                item,
                {
                  onActivate: activateForItem,
                  onCancel: cancelForItem,
                  onOpenDeposit: openDepositForBonus,
                  onOpenDetails: openBonusDetails,
                  onPlay: playForItem,
                  wallet,
                },
                formatter,
              );
            return (
              <ProfileBonusCardWithLiveTimer
                {...cardProps}
                IconComponent={Icon}
                LabelComponent={Label}
                countdownEndMs={countdownEndMs}
                hideCancelButton={hideBonusCancel}
                key={item.key}
                showProductType={!hideSportBonuses}
                title={<BonusTitle name={name} nameHighlighted={nameHighlighted} />}
              />
            );
          })}
          {hasNextPage && <div aria-hidden className="bonus-list-sentry" ref={sentryRef} />}
        </div>
      )}
    </>
  );
};

BonusList.propTypes = {
  activateBonus: PropTypes.func,
  activateFreespin: PropTypes.func,
  availableTemplates: PropTypes.array.isRequired,
  cancelFreespin: PropTypes.func,
  clientBonuses: PropTypes.array.isRequired,
  deactivateBonus: PropTypes.func,
  freespins: PropTypes.array,
  hideEmptyState: PropTypes.bool,
  wallet: PropTypes.object.isRequired,
};

export default BonusList;
